import React from 'react';

/**
 * Premium vertical chord diagram (method-book style).
 *
 * Props:
 *   positions: { [string: 1-6]: fret }       — 1 = high E (right), 6 = low E (left)
 *   fingers:   { [string: 1-6]: 1..4 }       — finger number shown in each dot
 *   muted:     [stringNumbers]               — strings not played (X on top)
 *   baseFret?: number (default 1)            — starting fret for barré chords
 *   title?:    string                        — optional title above diagram
 *
 * Conventions:
 *   - Strings drawn left → right as: 6 (Mi grave), 5 (La), 4 (Ré), 3 (Sol), 2 (Si), 1 (Mi aigu)
 *   - Frets are horizontal lines; nut (top thick bar) shown when baseFret === 1
 *   - Open strings (not in positions, not in muted) → "O" above
 *   - Muted strings → "X" above (red tone)
 *   - Finger numbers: 1=Index, 2=Majeur, 3=Annulaire, 4=Auriculaire
 */
const STRING_NAMES = ['Mi', 'La', 'Ré', 'Sol', 'Si', 'Mi']; // left→right (6,5,4,3,2,1)

const GuitarNeck = ({
  positions = {},
  fingers = {},
  muted = [],
  baseFret = 1,
  title,
  showStringNames = true,
  size = 'md',
}) => {
  // Layout constants (SVG units)
  const STRINGS = 6;
  const FRETS = 5;

  const sizeMap = {
    xs: { cellX: 11, cellY: 14 },
    sm: { cellX: 22, cellY: 28 },
    md: { cellX: 34, cellY: 42 },
    lg: { cellX: 42, cellY: 52 },
  };
  const { cellX, cellY } = sizeMap[size] || sizeMap.md;

  // Stroke widths and dot radii were originally tuned for size='sm'
  // (cellX=22) with absolute pixel values. At size='xs' (cellX=11),
  // those same values look proportionally TWICE as thick, which makes
  // the small diagrams feel heavy. We scale strokes by cellX/22 so xs
  // stays delicate while sm/md/lg keep their current weight.
  const sf = cellX / 22; // stroke scale factor (named `sf` to avoid
                          // shadowing the loop variable `s` used below
                          // for string numbers in iteration callbacks).
  const strokes = {
    nutHeight: Math.max(2, 5 * sf),       // top nut bar
    fret: Math.max(0.6, 1 * sf),          // horizontal frets
    stringThick: Math.max(0.7, 1.6 * sf), // bass strings
    stringThin: Math.max(0.5, 1 * sf),    // treble strings
    openCircle: Math.max(0.9, 1.6 * sf),  // O above the nut
    mutedX: Math.max(1.1, 2 * sf),        // X above the nut
    dotStroke: Math.max(0.8, 1.5 * sf),   // gold ring around finger dots
    topLine: Math.max(0.7, 1.2 * sf),     // top line when not nut
  };

  const PAD_X = cellX * 1.3;
  const PAD_TOP = cellY * 1.6; // room for O/X and fret label
  const PAD_BOTTOM = cellY * 1.2; // room for string names

  const neckWidth = (STRINGS - 1) * cellX;
  const neckHeight = FRETS * cellY;
  const width = neckWidth + PAD_X * 2;
  const height = neckHeight + PAD_TOP + PAD_BOTTOM;

  const X_OF_STRING = (stringNum) => {
    // stringNum 6 → left, stringNum 1 → right
    const idxFromLeft = STRINGS - stringNum; // 0..5
    return PAD_X + idxFromLeft * cellX;
  };
  const Y_OF_FRET_CENTER = (fretFromBase) =>
    PAD_TOP + (fretFromBase - 0.5) * cellY;
  const Y_OF_FRET_LINE = (fretFromBase) => PAD_TOP + fretFromBase * cellY;

  // Determine which strings are open (not muted, no pressed fret)
  const pressedStrings = new Set(Object.keys(positions).map(Number));
  const mutedSet = new Set(muted);
  const openStrings = [];
  for (let s = 1; s <= STRINGS; s++) {
    if (!pressedStrings.has(s) && !mutedSet.has(s)) openStrings.push(s);
  }

  const showNut = baseFret === 1;
  const primary = '#5D4E37';
  const accent = '#D4A574';
  const faded = '#5D4E37';
  const mutedColor = '#B0604A';

  return (
    <div className="w-full flex flex-col items-center">
      {title && (
        <p className="text-sm font-serif font-medium text-[#5D4E37] mb-2">{title}</p>
      )}
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full max-w-[320px] h-auto"
        role="img"
        aria-label={title ? `Diagramme de l'accord ${title}` : 'Diagramme d\'accord'}
      >
        {/* O / X markers above the nut */}
        {openStrings.map((s) => (
          <g key={`o-${s}`}>
            <circle
              cx={X_OF_STRING(s)}
              cy={PAD_TOP - cellY * 0.5}
              r={cellX * 0.28}
              fill="none"
              stroke={primary}
              strokeWidth={strokes.openCircle}
            />
          </g>
        ))}
        {muted.map((s) => (
          <g key={`x-${s}`}>
            <line
              x1={X_OF_STRING(s) - cellX * 0.22}
              y1={PAD_TOP - cellY * 0.75}
              x2={X_OF_STRING(s) + cellX * 0.22}
              y2={PAD_TOP - cellY * 0.25}
              stroke={mutedColor}
              strokeWidth={strokes.mutedX}
              strokeLinecap="round"
            />
            <line
              x1={X_OF_STRING(s) - cellX * 0.22}
              y1={PAD_TOP - cellY * 0.25}
              x2={X_OF_STRING(s) + cellX * 0.22}
              y2={PAD_TOP - cellY * 0.75}
              stroke={mutedColor}
              strokeWidth={strokes.mutedX}
              strokeLinecap="round"
            />
          </g>
        ))}

        {/* Base fret label for barré chords */}
        {!showNut && (
          <text
            x={PAD_X - cellX * 0.35}
            y={Y_OF_FRET_CENTER(1) + 4}
            fontSize={cellX * 0.4}
            fill={primary}
            textAnchor="end"
            fontFamily="Georgia, serif"
          >
            {baseFret}
          </text>
        )}

        {/* Nut (thick top bar) or open top line */}
        {showNut ? (
          <rect
            x={PAD_X - 1}
            y={PAD_TOP - strokes.nutHeight + 1}
            width={neckWidth + 2}
            height={strokes.nutHeight}
            fill={primary}
            rx={1}
          />
        ) : (
          <line
            x1={PAD_X}
            y1={PAD_TOP}
            x2={PAD_X + neckWidth}
            y2={PAD_TOP}
            stroke={primary}
            strokeWidth={strokes.topLine}
          />
        )}

        {/* Horizontal fret lines */}
        {Array.from({ length: FRETS }).map((_, i) => (
          <line
            key={`fret-${i}`}
            x1={PAD_X}
            y1={Y_OF_FRET_LINE(i + 1)}
            x2={PAD_X + neckWidth}
            y2={Y_OF_FRET_LINE(i + 1)}
            stroke={primary}
            strokeWidth={strokes.fret}
            opacity={0.6}
          />
        ))}

        {/* Vertical strings (low E left → high E right) */}
        {Array.from({ length: STRINGS }).map((_, i) => {
          const stringNum = STRINGS - i;
          const x = PAD_X + i * cellX;
          const thick = stringNum >= 4; // bass strings drawn slightly thicker
          return (
            <line
              key={`str-${stringNum}`}
              x1={x}
              y1={PAD_TOP}
              x2={x}
              y2={PAD_TOP + neckHeight}
              stroke={primary}
              strokeWidth={thick ? strokes.stringThick : strokes.stringThin}
              opacity={0.75}
            />
          );
        })}

        {/* Finger dots with finger number */}
        {Object.entries(positions).map(([strNum, fret]) => {
          const sNum = parseInt(strNum, 10);
          const fretFromBase = fret - baseFret + 1;
          if (fretFromBase < 1 || fretFromBase > FRETS) return null;
          const x = X_OF_STRING(sNum);
          const y = Y_OF_FRET_CENTER(fretFromBase);
          const finger = fingers[sNum];
          return (
            <g key={`dot-${sNum}`}>
              <circle
                cx={x}
                cy={y}
                r={cellX * 0.36}
                fill={primary}
                stroke={accent}
                strokeWidth={strokes.dotStroke}
              />
              {finger !== undefined && (
                <text
                  x={x}
                  y={y}
                  fontSize={cellX * 0.42}
                  fill="#FFFFFF"
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontFamily="Georgia, serif"
                  fontWeight="600"
                >
                  {finger}
                </text>
              )}
            </g>
          );
        })}

        {/* String names at bottom */}
        {showStringNames && STRING_NAMES.map((label, i) => {
          const x = PAD_X + i * cellX;
          return (
            <text
              key={`str-name-${i}`}
              x={x}
              y={PAD_TOP + neckHeight + cellY * 0.7}
              fontSize={cellX * 0.36}
              fill={faded}
              textAnchor="middle"
              fontFamily="Georgia, serif"
              opacity={0.7}
            >
              {label}
            </text>
          );
        })}
      </svg>
    </div>
  );
};

export default GuitarNeck;
